import { ROLES } from '@app/permissions/roles';
import { selectCurrentUserRole } from '@features/auth/store/selectors';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, FormControl, InputLabel, MenuItem, Select, TextField, Typography, useTheme } from '@mui/material';
import useDeleteNode from '@pages/schemes/hooks/useDeleteNode';
import StyledMenu from '@shared/components/ui/StyledMenu';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { matchesIpRegex } from '@shared/utils/utilsOld';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useEdges, useReactFlow } from 'reactflow';

const ZoneConfigMenu = ({
    anchorEl,
    setAnchorEl,
    nodeId,
    zoneType,
    setZoneType,
    zoneIndex,
    setZoneIndex,
    zoneNetwork,
    setZoneNetwork,
}) => {
    const { t } = useTranslation();
    const { getNode, setNodes } = useReactFlow();
    const theme = useTheme();
    const open = Boolean(anchorEl);
    const [zoneNetworkInputError, setZoneNetworkInputError] = useState(false);
    const edges = useEdges();
    const node = useMemo(() => getNode(nodeId), [nodeId, getNode]);
    const role = useSelector(selectCurrentUserRole);

    useEffect(() => {
        if (!open && zoneNetworkInputError) {
            setZoneNetwork(node.data.network ?? '');
            setZoneNetworkInputError(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleClose = () => {
        setAnchorEl(null);
        setNodes((nodes) => nodes.map((n) => ({ ...n, selected: false })));
    };

    const handleChangeZoneIndex = (e) => {
        const value = Number(e.target.value);
        if (value > 99) {
            setZoneIndex(99);
            return;
        }
        if (value < 1) {
            setZoneIndex(1);
            return;
        }
        setZoneIndex(value);
    };

    const handleChangeZoneNetwork = (e) => {
        const value = e.target.value;
        setZoneNetwork(value);
    };

    const handleZoneNetworkInputBlur = (e) => {
        const value = e.target.value;

        if (value && !matchesIpRegex(value, 'subnet')) {
            setZoneNetworkInputError(true);
            return;
        }

        if (value) {
            node.data.network = value;
            setNodes((n) => structuredClone(n));
        } else {
            // TODO delete all "ip_address" data of connected ports to this node
            const nodesPortsToDeleteIpAddresses = {};

            for (const edge of edges) {
                const connectedZoneNode = getNode(edge.target);

                if (!connectedZoneNode) {
                    continue;
                }

                if (connectedZoneNode.id === nodeId) {
                    nodesPortsToDeleteIpAddresses[edge.source] = nodesPortsToDeleteIpAddresses[edge.source]
                        ? [...nodesPortsToDeleteIpAddresses[edge.source], edge.sourceHandle]
                        : [edge.sourceHandle];
                }
            }

            setNodes((nodes) =>
                nodes.map((n) => {
                    if (!nodesPortsToDeleteIpAddresses[n.id]) {
                        return n;
                    }

                    const nodeCopy = structuredClone(n);

                    nodeCopy.data.ports = nodeCopy.data.ports.map((port) => {
                        if (nodesPortsToDeleteIpAddresses[n.id].includes(port.name)) {
                            delete port['ip_address'];
                        }

                        return port;
                    });

                    return nodeCopy;
                }),
            );

            delete node.data.network;
        }

        setZoneNetworkInputError(false);
    };

    return (
        <StyledMenu
            PaperProps={{ sx: { minWidth: '300px' } }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            verticalAnchorOrigin="bottom"
            horizontalAlign="left"
        >
            <Typography component="h5" sx={{ textAlign: 'center', margin: '5px', fontWeight: 600, fontSize: '0.8rem' }}>
                {t('zone_config_menu', { zone: node.type.toUpperCase() })}
            </Typography>
            <Box class="reactflow-node-config-menu">
                <Box
                    class="reactflow-node-config-menu__tab-content"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                        borderRadius: '4px 0 4px 4px',
                        padding: '12px 8px',
                    }}
                >
                    {' '}
                    <FormControl
                        size="small"
                        sx={{
                            backgroundColor: theme.palette.mode === 'light' ? 'white' : 'transparent',
                            width: 145,
                        }}
                    >
                        <InputLabel id="zone-type-label">{t('zone-type')}</InputLabel>
                        <Select
                            labelId="zone-type-label"
                            value={zoneType}
                            label={t('zone-type')}
                            onChange={(e) => setZoneType(e.target.value)}
                        >
                            <MenuItem value={'static'}>static</MenuItem>
                            <MenuItem value={'dynamic'}>dynamic</MenuItem>
                            {node.type !== 'mgmt' && <MenuItem value={'trunking'}>trunking</MenuItem>}
                        </Select>
                    </FormControl>
                    {zoneType !== 'static' && (
                        <>
                            <TextField
                                label={t('index')}
                                size="small"
                                type="number"
                                sx={{
                                    backgroundColor: theme.palette.mode === 'light' ? 'white' : 'transparent',
                                    width: 145,
                                }}
                                value={zoneIndex}
                                onChange={handleChangeZoneIndex}
                                InputProps={{
                                    inputProps: {
                                        min: 1,
                                        max: 99,
                                    },
                                }}
                            />
                            {(role === ROLES.TEACHER || role === ROLES.ADMIN || role === ROLES.SYS_ADMIN) && (
                                <TextField
                                    label={t('network')}
                                    size="small"
                                    sx={{
                                        backgroundColor: theme.palette.mode === 'light' ? 'white' : 'transparent',
                                        width: 145,
                                        '& .MuiFormHelperText-root': {
                                            background: 'var(--custom-textbox-background-color)',
                                            margin: 0,
                                        },
                                    }}
                                    placeholder="Ex. 192.168.70.0/24"
                                    value={zoneNetwork}
                                    onChange={handleChangeZoneNetwork}
                                    onBlur={handleZoneNetworkInputBlur}
                                    error={zoneNetworkInputError}
                                    helperText={zoneNetworkInputError && t('zone_network_helper_text')}
                                />
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </StyledMenu>
    );
};

const ZoneToolbar = ({
    nodeId,
    hidden,
    zoneType,
    setZoneType,
    zoneIndex,
    setZoneIndex,
    zoneNetwork,
    setZoneNetwork,
}) => {
    const { handleDeleteNode } = useDeleteNode();
    const { getNode, getEdges } = useReactFlow();
    const edges = getEdges();
    const node = useMemo(() => getNode(nodeId), [nodeId, getNode]);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const menuOpen = Boolean(menuAnchorEl);
    const menuOpenRef = useRef();
    const isMgmtNode = node.type === 'mgmt';

    const canBeConfigured = useMemo(() => {
        const isCreatedByHW = Boolean(node.data.connectedHwIds?.length);
        const hasConnectionWithHW = edges.some((edge) => {
            const isConnectionToCurrentZone = edge.target === node.id;
            const sourceNode = getNode(edge.source);

            if (!sourceNode) {
                return false;
            }

            return isConnectionToCurrentZone && sourceNode.data.hw_id;
        });

        return !(isCreatedByHW || hasConnectionWithHW);
    }, [edges, getNode, node.data.connectedHwIds?.length, node.id]);

    const handleOpenConfigMenu = (event) => {
        event.preventDefault();
        setMenuAnchorEl(menuOpenRef.current);
    };

    return (
        <>
            <Box
                sx={{
                    position: 'absolute',
                    top: -22,
                    right: isMgmtNode ? -22 : -40,
                    cursor: 'pointer',
                    display: 'flex',
                    gap: 0.5,
                }}
            >
                {canBeConfigured && (
                    <UtmsButton
                        ref={menuOpenRef}
                        disabled={menuOpen}
                        size="small"
                        sx={{
                            visibility: hidden ? 'hidden' : '',
                            minWidth: '13px',
                            minHeight: '13px',
                            borderRadius: 1,
                            padding: 0,
                        }}
                        variant="contained"
                        color="info"
                        onClick={handleOpenConfigMenu}
                    >
                        <SettingsIcon
                            sx={{
                                width: '1.2em',
                                height: '1.2em',
                                fontSize: 9,
                                transform: menuOpen ? 'rotate(90deg)' : '',
                                transition: 'transform 0.3s ease-in',
                            }}
                        />
                    </UtmsButton>
                )}
                {node.deletable !== false && (
                    <UtmsButton
                        className="delete-zone-btn"
                        size="small"
                        sx={{
                            visibility: hidden ? 'hidden' : '',
                            minWidth: '13px',
                            minHeight: '13px',
                            borderRadius: 1,
                            padding: 0,
                        }}
                        variant="contained"
                        color="error"
                        onClick={(_e) => handleDeleteNode(node, 'button')}
                    >
                        <DeleteOutlineIcon color="white" sx={{ fontSize: 9, width: '1.25em', height: '1.25em' }} />
                    </UtmsButton>
                )}
            </Box>
            <ZoneConfigMenu
                anchorEl={menuAnchorEl}
                setAnchorEl={setMenuAnchorEl}
                nodeId={nodeId}
                zoneType={zoneType}
                setZoneType={setZoneType}
                zoneIndex={zoneIndex}
                setZoneIndex={setZoneIndex}
                zoneNetwork={zoneNetwork}
                setZoneNetwork={setZoneNetwork}
            />
        </>
    );
};

export default ZoneToolbar;
